import React, { useState, useEffect } from 'react';
import {
  ALL_SHARD_MODULES,
  SuperTable,
  getTranslatedText
} from '@nile/components';
import styled from 'styled-components';
import envConfig from '@nile/home/src/Config/env';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIntl } from 'react-intl';
import LinkCell from './LinkCell';
import ToggleCell from './ToggleCell';
import DependentFieldCell from './DependantFieldCell';
import ActionCell from './ActionCell.tsx';

const FieldTableWrapper = styled.div`
.table-title {
  font-size: 18px;
  margin-bottom: 15px;
  padding-left: 40px;
  font-weight: 600;
}
.disabled-cell {
  display: inline;
  position: relative;
  left: 5px
  opacity: 0.5
}
.superTable {

  height: 100%

  .supertip-wrapper {
    position: relative;
    top: 2px;
  }

  .noDataCard {
    top: -29px;
  }
  thead tr :not(:first-child) {
    .header-inner {
      justify-content: center;
    }
  }
  thead tr th:first-child {
    .header-wrapper {
      padding-left: 30px;
    }
  }
  tbody tr td:first-child {
    padding-left: 40px;
  }

  position: relative;
  .Link-cell button {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .toggle-cell {
    display: inline;
    position: relative;
    left: 140px;
  }
}
`;

const FieldsTable = (props) => {
  const {
    data,
    title,
    module,
    loading,
    actions,
    disableEdit,
    isDeleteAuthorised,
    deleteField,
    toggleFieldStatus,
    checkDisabledRow,
    constraints,
    hideShowToClients,
    columnsToHide,
    isGroupingEnabled
  } = props;
  const intl = useIntl();
  const flags = useFlags();

  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'name', columns: [] }
  ]);

  const defaultColumnCells = {
    Link: LinkCell,
    Toggle: ToggleCell,
    EmptyCell: <></>,
    Dependent: DependentFieldCell,
    Action: ActionCell
  };

  const fetchViewData = () => {
    const cols = [
      {
        name: 'fields',
        label: getTranslatedText(
          intl,
          'admin.mandatoryFieldsTable.header.fields'
        ),
        dataType: 'Link',
        type: 'Link',
        action: actions?.updateField,
        disableEdit: disableEdit,
        module: module || ALL_SHARD_MODULES.ticket
      },
      {
        name: 'mandatoryOnCreate',
        label: getTranslatedText(
          intl,
          'admin.mandatoryFieldsTable.header.mandatoryOnCreate'
        ),
        type: 'Toggle',
        action: actions?.updateField,
        disableEdit: disableEdit
      },
      {
        name: 'mandatoryOnClosure',
        label: getTranslatedText(
          intl,
          'admin.mandatoryFieldsTable.header.mandatoryOnClose'
        ),
        type: 'Toggle',
        action: actions?.updateField,
        disableEdit: disableEdit
      }
    ];

    if (flags?.dependentField) {
      cols.splice(1, 0, {
        name: 'parentField',
        size: 70,
        label: getTranslatedText(intl, 'admin.customFields.dependent.field'),
        type: 'Dependent'
      });
    }
    if (!hideShowToClients) {
      cols.push({
        name: 'showToClient',
        label: getTranslatedText(
          intl,
          'admin.mandatoryFieldsTable.header.visibleToRequester'
        ),
        type: 'Toggle',
        action: actions?.updateField,
        disableEdit: disableEdit
      });
    }
    const columns = [];
    cols.forEach((col) => {
      const isColumnHidden = columnsToHide && columnsToHide.includes(col.name);
      if (!isColumnHidden) {
        columns.push({
          Header: col.label,
          header: col.label,
          accessorKey: col.name,
          minSize: 195,
          type: col.type,
          id: col.name,
          key: col.name,
          disableResizing: true,
          className: 'sticky',
          disableSortBy: true,
          editable: false,
          disableFilters: true,
          dataType: col.dataType,
          // actions: getActions(col),
          field: col,
          module: module,
          constraints: constraints
        });
      }
    });

    if (hideShowToClients) {
      columns.push({
        header: ' ',
        name: 'emptyCell',
        accessorKey: 'emptyCell',
        size: 195,
        id: 'emptyCell',
        type: 'EmptyCell',
        disableSortBy: true,
        disableFilters: true,
        disableResizing: true
      });
    }

    if (!disableEdit || isDeleteAuthorised) {
      columns.push({
        header: ' ',
        accessorKey: 'delete',
        name: 'delete',
        labels: {
          description: getTranslatedText(
            intl,
            'admin.CustomFields.delete.confirm'
          ),
          title: getTranslatedText(intl, 'admin.CustomFields.delete.text')
        },
        minSize: 60,
        maxSize: 60,
        id: 'delete',
        pinned: 'right',
        type: 'Action',
        action: true,
        editable: !disableEdit,
        deleteable: isDeleteAuthorised,
        disableSortBy: true,
        disableFilters: true,
        field: { name: 'delete' },
        actions: {
          toggleFieldStatus: toggleFieldStatus,
          deleteField: deleteField,
          checkDeleteItemAllowPermission:
            actions?.checkDeleteItemAllowPermission
        },
        module: module
        // enablePin: true,
        // defaultPin: 'right',
        // disableUnpin: true
      });
    } else {
      columns.push({
        header: ' ',
        name: 'emptyCell',
        accessorKey: 'emptyCell',
        size: 40,
        id: 'emptyCell',
        pinned: 'right',
        type: 'EmptyCell',
        disableSortBy: true,
        disableFilters: true
      });
    }

    setColumnGroup([
      {
        Header: 'Name',
        header: 'Name',
        name: ' ',
        columns: [...columns]
      }
    ]);
  };

  useEffect(() => {
    fetchViewData();
  }, [data]);

  return (
    <>
      <FieldTableWrapper>
        <p className="table-title">{title}</p>
        <SuperTable
          context={{
            records: [],
            viewConditions: null
          }}
          data={data}
          columns={[...columnGroup]}
          defaultColumnCells={defaultColumnCells}
          emptyState={{
            nodataImage: `${envConfig.imageRoot}svg/Invoice.svg`,
            heading: getTranslatedText(
              intl,
              'admin.mandatoryTicketFields.list.noData.heading'
            ),
            description: getTranslatedText(
              intl,
              'admin.mandatoryTicketFields.list.noData.description'
            )
          }}
          listInfo={{ totalCount: data && data.length }}
          noBulkSelect
          isLoading={loading}
          checkDisabledRow={checkDisabledRow}
          groupAccessor={({ status }) => {
            return {
              id: status,
              name: getTranslatedText(
                intl,
                `admin.fields.group.header.${status?.toLowerCase()}`
              ),
              empty: getTranslatedText(intl, 'NA')
            };
          }}
          isGroupingEnabled={isGroupingEnabled}
          defaultExpanded
          noOverscan
        />
      </FieldTableWrapper>
    </>
  );
};

export default FieldsTable;
